import { useState, useEffect } from "react";
import { useSim } from "@/context/SimContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Check, Pencil, X, OctagonX, AlertTriangle, ShieldAlert } from "lucide-react";

const COMPONENTS = ["Component A", "Component B", "Component C"];
const BINS = ["Bin 1", "Bin 2", "Bin 3"];

export default function DecisionModal() {
  const { state, decide, alertActive, acknowledge } = useSim();
  const pd = state?.pending_decision;
  const [correcting, setCorrecting] = useState(false);
  const [corr, setCorr] = useState("");
  const [corrDest, setCorrDest] = useState("");
  const [confirmStop, setConfirmStop] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (pd) {
      setCorrecting(false);
      setConfirmStop(false);
      setCorr(pd.ai_recommendation !== "UNKNOWN" ? pd.ai_recommendation : "Component A");
      setCorrDest(pd.destination || "Bin 1");
    }
  }, [pd?.event_no]);

  if (!pd) return null;
  const isSafety = pd.kind === "safety_event";

  const act = async (decision, extra = {}) => {
    setBusy(true);
    try {
      const res = await decide({ decision, ...extra });
      toast.success(`Decision recorded: ${decision} · ${res.result} (${res.event_no})`);
    } catch (e) {
      toast.error("Failed to submit decision");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!pd}>
      <DialogContent
        onMouseDown={acknowledge}
        className={`bg-[#0A1128] text-slate-100 max-w-2xl p-0 gap-0 [&>button]:hidden border ${
          alertActive
            ? (isSafety ? "ngx-alert-glow-red" : "ngx-alert-glow") + " border-transparent"
            : "border-slate-700"
        }`}
        data-testid="decision-modal"
      >
        <DialogHeader className={`p-5 border-b ${isSafety ? "border-red-800 bg-red-950/30" : "border-amber-800/50 bg-amber-950/20"}`}>
          <DialogTitle className="flex items-center gap-3">
            {isSafety
              ? <ShieldAlert className="h-6 w-6 text-red-400" />
              : <AlertTriangle className="h-6 w-6 text-amber-400" />}
            <span className={`font-display text-xl font-bold tracking-tight ${isSafety ? "text-red-300" : "text-amber-300"}`}>
              {isSafety ? "OPERATION PAUSED — SAFETY REVIEW" : "HUMAN DECISION REQUIRED"}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="p-5 grid grid-cols-2 gap-5">
          {/* Camera representation */}
          <div className="relative rounded-md overflow-hidden border border-slate-700 bg-black aspect-[4/3]">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/92690209-b493-4230-bc74-6cd11c8ee74a/images/58e433b703cec17e9654c3febb7b6f3dd462aca3fad79a26319ea3984035a9d6.jpeg"
              alt="vision feed" className="w-full h-full object-cover opacity-90"
            />
            <div className="absolute inset-0 ngx-grid-overlay opacity-30" />
            <div className="absolute border-2 border-amber-400 rounded-sm"
                 style={{ left: "28%", top: "30%", width: "40%", height: "38%" }}>
              <span className="absolute -top-6 left-0 text-[10px] font-mono bg-amber-400 text-black px-1.5 py-0.5 rounded-sm">
                {pd.detected_object} · {pd.confidence}%
              </span>
            </div>
            <div className="absolute top-2 left-2 text-[10px] font-mono text-emerald-400 flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 ngx-pulse" /> VISION FEED
            </div>
          </div>

          {/* Details */}
          <div className="space-y-3 font-mono text-sm">
            <Field label="Event ID" value={pd.event_no} />
            <Field label="AI Recommendation" value={pd.ai_recommendation}
                   valueClass={pd.ai_recommendation === "UNKNOWN" ? "text-amber-400" : "text-slate-100"} />
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Confidence</div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 rounded-full bg-slate-800 overflow-hidden">
                  <div className={`h-full ${pd.confidence < pd.threshold ? "bg-amber-500" : "bg-emerald-500"}`}
                       style={{ width: `${pd.confidence}%` }} />
                </div>
                <span className={pd.confidence < pd.threshold ? "text-amber-400" : "text-emerald-400"}>{pd.confidence}%</span>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">Threshold: {pd.threshold}%</div>
            </div>
            {pd.destination && <Field label="Proposed Destination" value={pd.destination} />}
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Reason</div>
              <p className="text-xs text-slate-300 leading-relaxed font-sans">{pd.reason}</p>
            </div>
          </div>
        </div>

        {/* Correction panel */}
        {correcting && (
          <div className="px-5 pb-2 grid grid-cols-2 gap-4 ngx-fade-in">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400">Corrected Classification</label>
              <Select value={corr} onValueChange={setCorr}>
                <SelectTrigger data-testid="correct-classification" className="mt-1.5 h-11 bg-[#050B14] border-slate-700 font-mono">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A1128] border-slate-700 text-slate-100">
                  {COMPONENTS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400">Corrected Destination</label>
              <Select value={corrDest} onValueChange={setCorrDest}>
                <SelectTrigger data-testid="correct-destination" className="mt-1.5 h-11 bg-[#050B14] border-slate-700 font-mono">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A1128] border-slate-700 text-slate-100">
                  {BINS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <DialogFooter className="p-5 border-t border-slate-800 grid grid-cols-4 gap-3 sm:gap-3">
          {!correcting ? (
            <>
              <Button data-testid="btn-approve" disabled={busy || isSafety}
                onClick={() => act("APPROVE")}
                className="h-14 flex-col gap-1 bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40">
                <Check className="h-4 w-4" /> APPROVE
              </Button>
              <Button data-testid="btn-correct" disabled={busy || isSafety}
                onClick={() => setCorrecting(true)}
                className="h-14 flex-col gap-1 bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40">
                <Pencil className="h-4 w-4" /> CORRECT
              </Button>
              <Button data-testid="btn-reject" disabled={busy || isSafety}
                onClick={() => act("REJECT")}
                className="h-14 flex-col gap-1 bg-red-600 hover:bg-red-700 text-white disabled:opacity-40">
                <X className="h-4 w-4" /> REJECT
              </Button>
              <Button data-testid="btn-stop" disabled={busy}
                onClick={() => setConfirmStop(true)}
                className="h-14 flex-col gap-1 bg-red-900 hover:bg-red-800 border-2 border-red-500 text-white font-bold">
                <OctagonX className="h-4 w-4" /> STOP
              </Button>
            </>
          ) : (
            <div className="col-span-4 flex gap-3">
              <Button data-testid="btn-cancel-correct" onClick={() => setCorrecting(false)}
                className="flex-1 h-12 bg-slate-800 hover:bg-slate-700 text-slate-200">
                Cancel
              </Button>
              <Button data-testid="btn-confirm-correct" disabled={busy}
                onClick={() => act("CORRECT", { correction: corr, correction_destination: corrDest })}
                className="flex-1 h-12 bg-blue-600 hover:bg-blue-700 text-white">
                Confirm Correction → {corr} / {corrDest}
              </Button>
            </div>
          )}
        </DialogFooter>

        {isSafety && !correcting && (
          <div className="px-5 pb-5 -mt-2 space-y-3">
            <p className="text-[11px] text-red-300/80 font-mono leading-relaxed" data-testid="safety-disclaimer">
              NGX simulation safety controls do not replace certified industrial safety systems.
            </p>
            <Button data-testid="btn-safety-resume" disabled={busy}
              onClick={() => act("APPROVE")}
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-medium">
              <Check className="h-4 w-4 mr-2" /> Review Complete — Resume Operation
            </Button>
          </div>
        )}

        {/* STOP confirmation */}
        {confirmStop && (
          <div className="absolute inset-0 bg-[#050B14]/95 flex items-center justify-center rounded-lg ngx-fade-in" data-testid="stop-confirm">
            <div className="text-center max-w-sm p-6">
              <OctagonX className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="font-display text-xl font-bold text-red-300">Confirm Emergency Stop</h3>
              <p className="text-sm text-slate-400 mt-2 mb-6">
                This will immediately halt the simulated process. Confirm this safety-critical action.
              </p>
              <div className="flex gap-3">
                <Button data-testid="btn-stop-cancel" onClick={() => setConfirmStop(false)}
                  className="flex-1 h-12 bg-slate-800 hover:bg-slate-700 text-slate-200">Cancel</Button>
                <Button data-testid="btn-stop-confirm" disabled={busy}
                  onClick={() => act("STOP")}
                  className="flex-1 h-12 bg-red-700 hover:bg-red-600 border border-red-500 text-white font-bold uppercase tracking-wider">
                  Confirm Stop
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, valueClass = "text-slate-100" }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-0.5">{label}</div>
      <div className={`text-base ${valueClass}`}>{value}</div>
    </div>
  );
}
